// eslint-disable-next-line no-restricted-imports
import { Container, FlexContainer, Divider } from "@carrotfertility/carotene" // please do not import from @carrotfertility/carotene after eslint rule added on 3/20/25
import ArticleThumbnail from "./ArticleThumbnail"
import React, { useEffect } from "react"
import { useContentfulConditionalGate } from "../../lib/contentful/components/ContentfulConditionGate"
import slugify from "slugify"
import { useReadLearnLinks } from "./shared/ReadLearnLinks"
import { useWindowEvent } from "../../utils/hooks/useWindowEvent"
import { Grid, InternalIcon, Link, Typography } from "@carrotfertility/carotene-core"
import { FormattedMessage } from "react-intl"
import { Link as RouterLink, useLocation } from "react-router-dom"
import { EntryWithoutUnresolvableLinks } from "#/utils/ContentTypes"
import { TypeCategorySkeleton, TypeSubCategorySkeleton } from "#/types/contentful"
import { mapContentToCategory } from "./shared/CategoryMapper"

type SubcategorySectionProps = {
  subCategory: EntryWithoutUnresolvableLinks<TypeSubCategorySkeleton>
  category: EntryWithoutUnresolvableLinks<TypeCategorySkeleton>
  isLast: boolean
}

export function SubcategorySection({ subCategory, category, isLast }: SubcategorySectionProps): JSX.Element {
  const { subCategoryPageLink } = useReadLearnLinks({ category, subCategory })
  const location = useLocation()
  const [windowWidth, setWindowWidth] = React.useState({
    width: window.innerWidth
  })
  const maxNumOfVisibleArticles = 6

  const handleResize = () => {
    setWindowWidth({
      width: window.innerWidth
    })
  }

  // Added because useAnchorLinkScrollFix in App.tsx doesn't wait until the library data is loaded on a full page refresh
  useEffect(() => {
    const slug = slugify(subCategory.fields.navTitle ?? subCategory.fields.title)
    const id = location?.hash.replace("#", "")
    if (id === slug) {
      const element = document.getElementById(slug)
      if (element) {
        element.scrollIntoView()
      }
    }
  }, [location.hash, subCategory.fields.navTitle, subCategory.fields.title])

  useWindowEvent("resize", handleResize)
  const articleAndConditions = React.useMemo(
    () =>
      subCategory?.fields?.articles?.map((article) => {
        const mappedCategory = mapContentToCategory(category)
        return {
          element: (
            <Grid item xs={12} sm={12} md={12} lg={windowWidth.width < 1200 ? 6 : 4} key={article.sys.id}>
              <ArticleThumbnail
                key={article?.sys?.id}
                contentType={article?.sys?.contentType?.sys?.id}
                article={article}
                category={mappedCategory}
              />
            </Grid>
          ),
          conditions: article?.fields?.conditions
        }
      }),
    [category, subCategory?.fields?.articles, windowWidth.width]
  )
  const filteredArticles = useContentfulConditionalGate(articleAndConditions, true)

  const getStackSpacing = (): string => {
    if (isLast) return "none"
    return filteredArticles.length > maxNumOfVisibleArticles ? "large" : "huge"
  }

  return (
    <Container id={slugify(subCategory.fields.navTitle ?? subCategory.fields.title)}>
      <Divider paddingTopBottom="tiny" stack="small" />
      <FlexContainer stack={"medium"} direction={"row"} justifyContent={"space-between"}>
        <Typography variant="h5">{subCategory.fields.title}</Typography>
      </FlexContainer>
      <Container stack={getStackSpacing()}>
        <Grid container spacing={4}>
          {filteredArticles.slice(0, maxNumOfVisibleArticles)}
        </Grid>
        <FlexContainer justifyContent="flex-end" padding="24px 0 0 0">
          {filteredArticles.length > maxNumOfVisibleArticles ? (
            <Link
              endIcon={<InternalIcon fontSize="small" color="primary" />}
              to={subCategoryPageLink}
              component={RouterLink}
            >
              <FormattedMessage
                defaultMessage="See all {articleCount, number} resources"
                values={{ articleCount: filteredArticles.length }}
              />
            </Link>
          ) : null}
        </FlexContainer>
      </Container>
    </Container>
  )
}
